<script>
    import SFS2X, { SFSObject, SFSArray } from "sfs2x-api";
    import { getTables } from '../controllers/APIController';
    import { onDestroy, onMount } from 'svelte';
    import UserProfile from '../components/UserProfile.svelte';
    import Chat from '../components/Chat.svelte';
    import LobbyTable from '../components/LobbyTable.svelte';
    import casinosStore from '../stores/casinosStore';
    import tablesStore from '../stores/tablesStore';
    import { getLobbyRoom, getSFS, initGlobalSFS, initSFS, joinLobby, updateLocation } from '../controllers/SFSController';
    import ProfilePanel from "../components/ProfilePanel.svelte";
    import { get } from 'svelte/store';
    import userStore from '../stores/userStore';
    import FriendsPanel from '../components/FriendsPanel.svelte';
    import { navigate } from "svelte-routing";
    import { showToast } from "../utils/Toast";

    export const UPDATE_BBJP_MSG = "pokerServer.updateBBJP";
    export const OBSERVE_LOBBY_FLOOR_CMD = "pokerClient.observeLobbyFloor";
    export const LEAVE_LOBBY_FLOOR_CMD = "pokerClient.leaveLobbyFloor";
    export const UPDATE_LOBBY_FLOOR_MSG = "pokerServer.updateLobbyFloor";
    export const LOBBY_JOIN_TABLE_EVENT_MSG = "pokerServer.lobbyJoinTable";
    export const LOBBY_LEAVE_TABLE_EVENT_MSG = "pokerServer.lobbyLeaveTable";
    
    export let casinoId;
    let casinoName;
    let tables;
    let chat;
    let profilePanel;
    let friendsPanel;
    let lobbyTables = [];
    let lobbyUsers = [];
    let isConnected;
    let sfs;
    let globalSfs;
    let bbjpAmount = 0;
    let currentFloor = 0;

    onMount(() => {        
        (async () => {
            let casinoObj = await casinosStore.getCasinoById(casinoId);
            casinoName = casinoObj.name;
            tables = await tablesStore.fetchTables(casinoId);
            bbjpAmount = casinoObj.bbjp_amount;
            
            //Override SFS host and port in case we're opening this from localhost, to make it work with local SFS
            if(window.location.href.indexOf("localhost") != -1)
            {
                casinoObj.mp_addr = "127.0.0.1";
                casinoObj.mp_port = "8080";
            }
            
            globalSfs = initGlobalSFS();
            sfs = initSFS(casinoObj.mp_addr, casinoObj.mp_port, onLoggedIn);
        })();
        return destroy;
    });

    function destroy()
    {
        setupListeners(false);

        leaveFloor(0);  //unsubscribe when exiting the lobby

        sfs = null;
        globalSfs = null;
    }

    function setupListeners(add)
    {
        if (add)
        {
            sfs.addEventListener(SFS2X.SFSEvent.USER_ENTER_ROOM, onUserEnter, this);
            sfs.addEventListener(SFS2X.SFSEvent.USER_EXIT_ROOM, onUserExit, this);
            sfs.addEventListener(SFS2X.SFSEvent.EXTENSION_RESPONSE, onServerMessage, this);
        }
        else
        {
            sfs.removeEventListener(SFS2X.SFSEvent.USER_ENTER_ROOM, onUserEnter, this);
            sfs.removeEventListener(SFS2X.SFSEvent.USER_EXIT_ROOM, onUserExit, this);
            sfs.removeEventListener(SFS2X.SFSEvent.EXTENSION_RESPONSE, onServerMessage, this);
        }
    }

    function onLoggedIn()
    {
        isConnected = true;
        console.log("onLoggedIn");
        joinLobby(casinoId, onJoinedLobby);
    }

    function onJoinedLobby()
    {
        console.log("onJoinedLobby");
        if(chat)
            chat.registerEvents();
        updateLocation({casino: casinoId});

        setupListeners(true);

        updateLobbyUsers();
        observeFloor(0);
    }

    function onUserEnter(e)
    {
        console.log("onUserEnter");
        updateLobbyUsers();
    }

    function onUserExit(e)
    {
        console.log("onUserExit");
        updateLobbyUsers();
    }

    function onServerMessage(e)
    {
        console.log("onServerMessage: " + e.cmd);

        switch(e.cmd)
        {
            case UPDATE_BBJP_MSG:
                updateBBJP(e.params);
                break;
            case UPDATE_LOBBY_FLOOR_MSG:
                updateLobbyFloor(e.params);
                break;
            case LOBBY_JOIN_TABLE_EVENT_MSG:
                lobbyJoinTable(e.params);
                break;
            case LOBBY_LEAVE_TABLE_EVENT_MSG:
                lobbyLeaveTable(e.params);
                break;
        }
    }

    //---------------------------------------------------------------------------------------------------

    function observeFloor(floorNo)
    {
        if (floorNo < 0)    //invalid floor
            return;
        
        let params = new SFSObject();
        params.putInt("casinoId", parseInt(casinoId));
        params.putInt("floorNo", floorNo);

        if (sfs.isConnected)
            sfs.send(new SFS2X.ExtensionRequest(OBSERVE_LOBBY_FLOOR_CMD, params));  //request a full lobby update + to be subscribed to changes on any room at this floor
    }

    function leaveFloor(floorNo)
    {
        if (floorNo < 0)    //invalid floor
            return;
        
        let params = new SFSObject();
        params.putInt("casinoId", parseInt(casinoId));
        params.putInt("floorNo", floorNo);

        if (sfs.isConnected)
            sfs.send(new SFS2X.ExtensionRequest(LEAVE_LOBBY_FLOOR_CMD, params));  //request to unsubscribe from room events at this floor
    }

    function updateBBJP(params)
    {
        bbjpAmount = params.getDouble("bbjpAmount");
    }

    function onViewMyProfile()
    {
        if (profilePanel)
            profilePanel.show(get(userStore));
    }

    function openFriendsPanel()
    {
        if (friendsPanel)
            friendsPanel.show();
    }

    function onOpenProfilePanel(event)
    {
        if (profilePanel)
            profilePanel.show(event.detail);
        if (friendsPanel)
            friendsPanel.hide();
    }

    function requestExitCasino()
    {
        leaveCasino();
        navigate("/");
    }

    export function leaveCasino()
    {
        let sfs = getSFS();
        if (sfs && sfs.isConnected)
            sfs.send(new SFS2X.LeaveRoomRequest());  //request to leave the current room
    }

    function updateLobbyUsers()
    {
        let newLobbyUsers = [];
        let sfsRoom = getLobbyRoom();
        let sfsUsers = sfsRoom.getUserList();

        for(let sfsUser of sfsUsers)
        {
            let name = sfsUser.getVariable("userName").value;
            let photo = sfsUser.getVariable("photo").value;
            newLobbyUsers.push({id: sfsUser.name, name: name, photo: photo});
        }

        lobbyUsers = newLobbyUsers;
    }

    function updateLobbyFloor(params)
    {
        let sfsTables = params.getSFSArray("tables");
        for(let i = 0; i<sfsTables.size(); i++)
        {
            let sfsTable = sfsTables.getSFSObject(i);
            if (sfsTable && casinoId == sfsTable.getInt("casinoId") && currentFloor == sfsTable.getInt("floor"))
            {
                let tableId = sfsTable.getInt("tableId");
                lobbyTables[tableId].updatePlayers(sfsTable.getSFSArray("players"));
            }
        }
    }

    function lobbyJoinTable(params)
    {
        let tableId = params.getInt("tableId");
        let userId = params.getUtfString("userId");
        let userName = params.getUtfString("userName");
        let photo = params.getUtfString("photo");
        let seatNo = params.getInt("seatNo");

        if (lobbyTables[tableId])
            lobbyTables[tableId].joinTable(userId, userName, photo, seatNo);
    }

    function lobbyLeaveTable(params)
    {
        let tableId = params.getInt("tableId");
        let userId = params.getUtfString("userId");
        
        if (lobbyTables[tableId])
            lobbyTables[tableId].leaveTable(userId);
    }


</script>

<ion-header>
    <ion-toolbar>
        <div class="hbox">
            <ion-title style="margin-bottom: 60px;">
                {casinoName} Lobby
                {#if isConnected}
                    <span class="sfsConnected">&nbsp;&nbsp;&nbsp;&nbsp; SFS Connected</span>
                {:else}
                    <span class="sfsDisconnected">&nbsp;&nbsp;&nbsp;&nbsp; SFS Disconnected</span>
                {/if}
            </ion-title>
            <div class="hbox" style="position: absolute; left: 10px; bottom: 5px;">
                <ion-button on:click={()=>requestExitCasino()}>Back to Map</ion-button>
            </div>
            <UserProfile on:editName={onViewMyProfile}/>
        </div>
    </ion-toolbar>
</ion-header>

<ion-content>
    <span class="bbjp">Bad Beat Jackpot: {bbjpAmount}</span>
    <div class="hbox">
        <div class="grid">
            {#if tables}
                {#each tables as table}
                    <LobbyTable  bind:this={lobbyTables[table.id]}  casinoId={casinoId} tableData={table} />
                {/each}
            {/if}
        </div>

        <div style="width: 300px; height: 100%;" class="vbox">
            <ion-tile>Users in Lobby</ion-tile>
            {#each lobbyUsers as lobbyUser, i}
                <div class="hbox" style="align-items: center; width: 100%">
                    <img style="width: 50px; height: 50px;" src={lobbyUser.photo} on:click={() => { onOpenProfilePanel({detail: {user_id: lobbyUser.id, name: lobbyUser.name, avatar: lobbyUser.photo}}); }}  alt={lobbyUser.id}>
                    <span style="width: 120px; text-align: left; margin-left: 10px;">{lobbyUser.name}</span>
                </div>
             {/each}
        </div>
    </div>

    <ProfilePanel bind:this={profilePanel} />
    <FriendsPanel bind:this={friendsPanel} />

</ion-content>

<ion-footer>
    {#if isConnected}
    <div style="display: flex; justify-content: flex-end; margin-right: 8px;">
        <ion-button on:click={openFriendsPanel} >Friends</ion-button>    
    </div>
    {/if}
    <Chat bind:this={chat} type="lobby" casinoId={casinoId} />
</ion-footer>

<style>
    .grid {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        padding: 5px;
        width: 100%;
    }
    
    .sfsConnected {
        position: relative;
        font-size: smaller;
        color: green;
    }

    .sfsDisconnected {
        position: relative;
        font-size: smaller;
        color: red;
    }

    .bbjp {
        position: absolute;
        left: 10px;
        top: 10px;
    }
</style>